import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Button,
  Container,
  CssBaseline,
  TextField,
  Typography,
} from "@material-ui/core";
import Lottie from "react-lottie";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import SecurityLottie from "../../assets/lottie/security.json";
import toastError from "../../errors/toastError";

import iconLight from "../../assets/miscPng/icon.png";
import iconDark from "../../assets/miscPng/icon-dark.png";

import DefaultValuesUtils from "../../utils/classes/DefaultValuesUtils";
import LottieUtils from "../../utils/classes/LottieUtils";

const useStyles = makeStyles((theme) => ({
  //  ****************
  //  ** Containers **
  //  ****************
  passwordResetValidationContainer: {
    width: "100vw",
    height: "90vh",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-around",
    alignItems: "center",
    marginTop: "5vh",
    userSelect: "none",
    name: theme.name,
    color: theme.palette.lottie.plainBackground,
  },

  imgContainer: {
    backgroundColor: theme.palette.lottie.plainBackground,
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "10px 0px 0px 10px",
    [theme.breakpoints.down("sm")]: { display: "none", },
  },

  passwordResetValidationForm: {
    backgroundColor: theme.palette.lottie.secondaryPlainBackground,
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "0px 10px 10px 0px",
  },

  //  ********************
  //  ** Form Container **
  //  ********************
  avatar: {
    margin: theme.spacing(0),
    width: "100px",
    height: "auto",
  },

  inputsContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: "1em",
  },

  textField: {
    width: "45px",

    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: theme.palette.lottie.secondaryPlainBackground, },
      "&:hover fieldset": { borderColor: theme.palette.lottie.secondaryPlainBackground, },
      "&.Mui-focused fieldset": { borderColor: "red", },
    },
  },

  textFieldInput: {
    color: theme.palette.lottie.secondaryPlainBackground,
    backgroundColor: theme.palette.lottie.plainBackground,
  },

  floatingButton: {
    margin: theme.spacing(3, 0, 2),
    transition: "transform 0.30s",
    "&:hover": { transform: "translateY(-5px)", },
  },

  secondaryButton: { color: theme.palette.lottie.plainBackground, },
}));

const PasswordResetValidation = () => {
  //  ***************
  //  ** Variables **
  //  ***************
  const classes = useStyles();
  const history = useHistory();

  const [codeCharacters, setCodeCharacters] = useState(["", "", "", "", ""]);
  const [isResendCodeButtonDisabled, setIsResendCodeButtonDisabled] = useState(false);

  const currentIcon = localStorage.getItem("theme") ? iconDark : iconLight;

  const hashParam = new URLSearchParams(window.location.search).get("hash");

  

  //  *****************
  //  ** Use Effects **
  //  *****************
  useEffect(() => { document.title = "BestZap"; }, []);

  useEffect(() => {
    const checkResetPasswordRequisition = async () => {
      if (!hashParam) {
        history.push(DefaultValuesUtils.ROUTES[106]);
        return;
      }
  
      try {
        const { data } = await api.get("/passwordReset", { params: { hash: hashParam } });
        
        if (data.type === "warning") {
          toast.info(i18n.t(`backendErrors.${data.msg}`));
          history.push(DefaultValuesUtils.ROUTES[106]);
        }
      } catch (exception) {
        console.log("Password Reset Validation Exception:", exception);
        toastError(exception);
        history.push(DefaultValuesUtils.ROUTES[106]);
      }
    };

    checkResetPasswordRequisition();
  }, [hashParam, history]);  



  //  ***************
  //  ** Functions **
  //  ***************
  const focusOnNextInput = (index) => {
    const nextInput = document.getElementById(`codeCharacter${index}`);
    if (nextInput) nextInput.focus();
  };

  const handleKeyDown = (event, index) => {
    const codeRegex = /^[A-Za-z0-9]$/;
    const isAlphanumeric = codeRegex.test(event.key);
    const isBackspace = event.key === "Backspace";

    if (event.key === "Enter") {
      document.getElementById("confirmButton").click();
      return;
    }
  
    if (isAlphanumeric || isBackspace) {
      setCodeCharacters(previousValue => {
        const updatedValue = [...previousValue];
  
        if (isAlphanumeric) updatedValue[index] = event.key.toUpperCase();
        else updatedValue[index] = "";
  
        return updatedValue;
      });
  
      if (isAlphanumeric && index < 4) focusOnNextInput(index + 1);
      else if (isBackspace && index > 0) focusOnNextInput(index - 1);
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
  
    const pastedData = event.clipboardData.getData("text");
    const pastedCharacters = pastedData.split("").slice(0, 5);

    setCodeCharacters((previousValue) => {
      const updatedValue = [...previousValue];
  
      pastedCharacters.forEach((char, index) => {
        if (/^[A-Za-z0-9]$/.test(char)) updatedValue[index] = char.toUpperCase();
      });
  
      return updatedValue;
    });
  
    const firstEmptyIndex = pastedCharacters.findIndex((_, index) => pastedCharacters[index + 1] === undefined);
    if (firstEmptyIndex >= 0 && firstEmptyIndex < 4) focusOnNextInput(firstEmptyIndex + 1);
    if (firstEmptyIndex >= 4) focusOnNextInput(4);
  };

  const handleValidateCode = async () => {
    try {
      const typedCode = codeCharacters.join("");

      if (typedCode.length !== 5) {
        toast.info(i18n.t("backendErrors.PASSWORD_RESET_INVALID_CODE"));
        return;
      }

      const { data } = await api.get("/passwordReset/validateCode", {
        params: { hash: hashParam, validationCode: typedCode }
      });

      if (data.type === "warning") {
        toast.info(i18n.t(`backendErrors.${data.msg}`));
        if (data.msg === "PASSWORD_RESET_EXPIRED") history.push(DefaultValuesUtils.ROUTES[106]);
        return;
      }

      history.push(`/passwordResetDefinition?hash=${hashParam}`);
    } catch (exception) {
      console.log("Password Reset Validation - Handle Validate Code Exception:", exception);
      toastError(exception);
    }
  };

  const handleResendValidationCode = async () => {
    try {
      if (isResendCodeButtonDisabled) return;

      setIsResendCodeButtonDisabled(true);

      const { data } = await api.post("/passwordReset/resendValidationCode", { hash: hashParam });
      
      if (data.type === "warning") {
        toast.info(i18n.t(`backendErrors.${data.msg}`));
        history.push(DefaultValuesUtils.ROUTES[106]);
      } else if (data.type === "success") {
        toast.success(i18n.t("passwordResetValidation.toasts.success"));
      }

      setIsResendCodeButtonDisabled(false);
    } catch (exception) {
      setIsResendCodeButtonDisabled(false);
      toastError(exception);
      console.log("Password Reset Validation - Handle Resend Validation Code Exceptoion:", exception);
    }
  };

  const handleTurnBackToLogin = () => {
    history.push(DefaultValuesUtils.ROUTES[101]);
  };
  



  //  ************
  //  ** Return **
  //  ************
  return (
    <Container className={classes.passwordResetValidationContainer} component="main">
      <CssBaseline />

      <div className={classes.imgContainer}>
        <Lottie
          options={{ ...LottieUtils.ANIMATION_PROPERTIES, animationData: SecurityLottie }}
          isClickToPauseDisabled={true}
          style={{ width: "80.00%", height: "80.00%", }}
        />
      </div>

      <div className={classes.passwordResetValidationForm}>
        <img className={classes.avatar} src={currentIcon} alt="BestZap Logo" />

        <Typography component="h1" variant="h3">
          {i18n.t("passwordReset.title")}
        </Typography>

        <Typography component="h2" variant="h6">
          <br />
          {i18n.t("passwordResetValidation.subtitle")}
          <br /><br />
        </Typography>

        <div className={classes.inputsContainer}>
          {[0, 1, 2, 3, 4].map((element) => (
            <TextField
              className={classes.textField}
              autoComplete="off"
              variant="outlined"
              margin="normal"
              type="text"

              id={`codeCharacter${element}`}
              name={`codeCharacter${element}`}
              value={codeCharacters[element]}
              onKeyDown={(event) => handleKeyDown(event, element)}
              onPaste={(event) => handlePaste(event)}

              InputProps={{ className: classes.textFieldInput, inputProps: { maxLength: 1 } }}
              autoFocus={element === 0}
            />
          ))}
        </div>

        <Button
          id="confirmButton"
          variant="contained"
          color="primary"
          style={{ width: "80%" }}
          className={`${classes.floatingButton} ${classes.secondaryButton}`}
          onClick={handleValidateCode}
        >
          {i18n.t("passwordResetValidation.buttons.validate")}
        </Button>

        <Button
          style={{ width: "80%" }}
          variant="outlined"
          color="primary"
          className={`${classes.floatingButton} ${classes.secondaryButton}`}
          onClick={handleResendValidationCode}
        >
          {i18n.t("passwordResetValidation.buttons.resendCode")}
        </Button>

        <Button
          style={{ width: "80%" }}
          variant="outlined"
          color="primary"
          className={`${classes.floatingButton} ${classes.secondaryButton}`}
          onClick={handleTurnBackToLogin}
        >
          {i18n.t("passwordResetValidation.buttons.turnBackToLogin")}
        </Button>
      </div>
    </Container>
  );
};

export default PasswordResetValidation;
